import React, { Component } from 'react'
import FieldLabel from './fieldLabel'
import SelectedOptions from './selectedOptions'
import Options from './options'
import Actions from './actions'
import styles from './styles/field-style'
import uploadController from 'upload-controller'
import FieldError from './fieldError'
import WrappedDayPicker from './wrappedDayPicker'
import FieldFileProgress from './fieldFileProgress'
import moment from 'moment'
import { connect } from 'cerebral-view-react'
import Tappable from 'react-tappable'

let clickEvent = false
let baseUploadUrl =
  window.location.href.indexOf('/account/') !== -1
    ? '/account/api/upload'
    : '/api/upload'

class Field extends Component {
  constructor(props) {
    super(props)
    this.files = []
  }

  hasValue() {
    const { field } = this.props

    if (field.value && field.value.length) {
      return true
    }
    if (field.query && field.query.length) {
      return true
    }

    if (this.isFile()) {
      if (field.value.name) {
        return true
      }
    }
    return false
  }

  hasError() {
    const { field } = this.props
    if (field.isTouched && field.errorMessage) {
      return true
    }
    return false
  }

  hasFocus() {
    const { field } = this.props
    let containerFocus = field.hasFocus

    if (containerFocus) {
      return true
    }
    return false
  }

  isFile() {
    const { field } = this.props
    if (field.type && field.type === 'file') {
      return true
    }
    return false
  }

  componentDidUpdate() {
    if (this.isFile() && this.files.length > 0) {
      const { field, signals, path } = this.props
      if (field.isValid) {
        uploadController(this.files, {
          url: baseUploadUrl,
          onProgress(event) {
            signals.user.uploadProgress({ ...event, path: path })
          },
          onEnd(event) {
            signals.user.uploadDone({ ...event, path: path })
          },
          onError(event) {
            signals.user.uploadError({ ...event, path: path })
          }
        })
        this.files = []
      }
    }
  }

  onChange(e, value) {
    e.preventDefault()
    e.stopPropagation()
    const { field, signals, path } = this.props
    if (!(this.isFile() || field.type === 'date')) {
      if (e && e.target) {
        value = e.target.value
      }
      if (field.options) {
        signals.client.filterOptions({
          field: path,
          query: value,
          touched: true
        })
      } else {
        signals.forms.fieldChanged({
          field: path,
          value: value,
          touched: true
        })
      }
    }
  }

  getInputType() {
    const { field } = this.props
    if (this.isFile() || field.type === 'date') {
      return 'text' // change to file and data because the upload and date is not handled by default
    } else {
      return field.type || 'text'
    }
  }

  selectFile(e) {
    e.preventDefault()
    e.stopPropagation()
    const { signals, path } = this.props
    if (e.target.files.length > 0) {
      let file = e.target.files[0]
      this.files = e.target.files
      signals.forms.fieldChanged({
        field: path,
        value: {
          size: file.size,
          name: file.name,
          uploadDone: false,
          percentComplete: null
        },
        touched: true
      })
    }
  }

  renderFile() {
    if (this.isFile()) {
      return (
        <input
          type={'file'}
          ref={'file'}
          onChange={e => this.selectFile(e)}
          style={{ display: 'none', paddingLeft: -1000, zIndex: -100 }}
        />
      )
    }
    return null
  }

  onClickInput(e) {
    e.preventDefault()
    e.stopPropagation()
    clickEvent = true

    const { signals, field, path, disabled } = this.props
    if (this.isFile()) {
      if (field.value.percentComplete && !field.value.uploadDone) {
        return
      }
      this.refs.file.click()
      signals.client.focusField({ focus: false })
    } else {
      if (disabled) return
      if (!field.hasFocus) {
        signals.client.focusField({ path: path, focus: true })
      }
      this.refs.field.focus()
    }
  }

  onInputFocus(e) {
    e.preventDefault()
    e.stopPropagation()

    if (!this.hasFocus() && !clickEvent) {
      this.onClickInput(e)
      if (this.refs.field.setSelectionRange) {
        let length = this.refs.field.value.length
        this.refs.field.setSelectionRange(length, length)
      }
    }
    clickEvent = false
  }

  render() {
    const {
      label,
      device,
      signals,
      field,
      selectedLocale,
      path,
      disabled,
      name,
      autoCompleteType
    } = this.props

    const containerFocus = field.hasFocus
    const hasFocus = this.hasFocus()
    const hasValue = this.hasValue()
    const hasError = this.hasError()
    const hasOptions = field.options && field.options.length > 0
    let value = field.value || ''
    let isFile = this.isFile()
    let isDate = field.type === 'date'
    let showErrorLine = false
    let readOnly = false
    if (!hasValue) {
      if (field.isRequired && field.isTouched) {
        showErrorLine = true
      }
    }

    if (isDate || isFile || disabled) {
      readOnly = true
    }

    if (field.options) {
      value = field.query || ''
      if (
        field.isTouched &&
        field.isRequired &&
        (field.value && field.value.length === 0)
      ) {
        showErrorLine = true
      }
    }

    if (isDate && hasValue) {
      value = moment(value).format('YYYY-MM-DD')
    }

    // clear out the value so selected can be painted above
    if (!hasFocus) {
      if (field.options && field.value && field.value.length > 0) {
        value = ' '
      }
    }

    if (isFile) {
      value = field.value.name || ''
      if (value.length > 48) {
        value = value.substring(0, 47) + '...'
      }
    }

    return (
      <Tappable
        style={styles.container(hasError)}
        onTap={e => this.onClickInput(e)}
        key={'container'}
      >
        <FieldLabel
          isFile={isFile}
          hasFocus={hasFocus}
          hasValue={hasValue}
          name={field.name}
          nameKey={label}
          isRequired={field.isRequired}
        />
        <input
          ref={'field'}
          onInput={e => this.onChange(e)}
          autoComplete={autoCompleteType}
          name={name}
          value={value}
          readOnly={readOnly}
          onFocus={e => this.onInputFocus(e)}
          required={field.isRequired || false}
          type={this.getInputType()}
          style={styles.input(hasFocus, showErrorLine, disabled)}
        />
        {this.renderFile()}
        <FieldFileProgress field={field} />
        <Actions
          path={path}
          preventClear={field.preventClear}
          isDate={isDate}
          signals={signals}
          hasFocus={hasFocus}
          hasValue={hasValue}
          isMultiple={field.isMultiple}
          hasOptions={hasOptions}
          isFile={isFile}
          disabled={readOnly}
        />
        <FieldError field={field} />
        <WrappedDayPicker
          selectedLocale={selectedLocale}
          field={field}
          path={path}
          hasFocus={hasFocus}
          signals={signals}
        />
        <SelectedOptions
          device={device}
          field={field}
          containerFocus={containerFocus}
          disabled={readOnly}
        />
        <Options
          field={field}
          path={path}
          containerFocus={containerFocus}
          signals={signals}
          readOnly={readOnly}
        />
      </Tappable>
    )
  }
}

Field.defaultProps = {
  type: 'text'
}

export default connect(props => {
  return {
    field: `${props.path}`,
    selectedLocale: 'client.selectedLocale',
    device: 'useragent.device.type'
  }
})(Field)



// WEBPACK FOOTER //
// ./src/components/inputs/field.js